<script setup>
import { computed, onMounted, ref } from 'vue';

const props = defineProps({
    csrfToken: { type: String, default: '' },
    contactAction: { type: String, default: '/contato' },
    flash: { type: String, default: '' },
    errors: { type: Array, default: () => [] },
    contact: {
        type: Object,
        default: () => ({
            email: 'erasmo@reinformatica.com',
            phone: '85 997380835',
            whatsapp: '5585997380835',
            facebook: 'https://www.facebook.com/reinformatica.ce',
            instagram: 'https://www.instagram.com/reinformatica',
        }),
    },
    services: { type: Array, default: () => [] },
    portfolioItems: { type: Array, default: () => [] },
    articles: { type: Array, default: () => [] },
});

const currentYear = new Date().getFullYear();
const activeService = ref(props.services[0]?.id || '');
const isScrolled = ref(false);

const activeServiceData = computed(() => props.services.find((service) => service.id === activeService.value) || props.services[0] || null);
const hasPortfolio = computed(() => props.portfolioItems.length > 0);
const hasArticles = computed(() => props.articles.length > 0);

function maskPhone(event) {
    const input = event.target;
    const digits = input.value.replace(/\D/g, '').slice(0, 11);
    const area = digits.slice(0, 2);
    const first = digits.length > 10 ? digits.slice(2, 7) : digits.slice(2, 6);
    const second = digits.length > 10 ? digits.slice(7, 11) : digits.slice(6, 10);
    input.value = [area ? `(${area}` : '', area.length === 2 ? ') ' : '', first, second ? `-${second}` : ''].join('');
}

function youtubeEmbed(url) {
    if (!url) {
        return '';
    }

    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : '';
}

onMounted(() => {
    const onScroll = () => {
        isScrolled.value = window.scrollY > 18;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
});
</script>

<template>
    <div class="app-dark">
        <header class="site-header fixed-top" :class="{ 'is-scrolled': isScrolled }">
            <nav class="navbar navbar-expand-lg">
                <div class="container">
                    <a class="navbar-brand brand-mark" href="#home" aria-label="R. E. Informática">
                        <img class="brand-logo" src="/images/logo.png" alt="R. E. Informática">
                    </a>
                    <button class="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#mainMenu" aria-controls="mainMenu" aria-expanded="false" aria-label="Abrir menu">
                        <span class="navbar-toggler-icon"></span>
                    </button>
                    <div id="mainMenu" class="collapse navbar-collapse">
                        <ul class="navbar-nav ms-auto align-items-lg-center gap-lg-2">
                            <li class="nav-item"><a class="nav-link" href="#home">Home</a></li>
                            <li class="nav-item"><a class="nav-link" href="#servicos">Serviços</a></li>
                            <li class="nav-item"><a class="nav-link" href="#portfolio">Portfólio</a></li>
                            <li class="nav-item"><a class="nav-link" href="#artigos">Artigos</a></li>
                            <li class="nav-item"><a class="nav-link" href="#contato">Contato</a></li>
                            <li class="nav-item social-links">
                                <a :href="contact.facebook" target="_blank" rel="noopener" aria-label="Facebook"><i class="fa-brands fa-facebook-f"></i></a>
                                <a :href="contact.instagram" target="_blank" rel="noopener" aria-label="Instagram"><i class="fa-brands fa-instagram"></i></a>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>
        </header>

        <main>
            <section id="home" class="hero-section">
                <div class="hero-overlay"></div>
                <div class="container hero-content">
                    <div class="row align-items-center g-5">
                        <div class="col-lg-7">
                            <p class="eyebrow">Tecnologia para empresas que precisam seguir operando</p>
                            <h1>TI, infraestrutura e web com suporte experiente.</h1>
                            <p class="hero-copy">A R. E. Informática une mais de 20 anos de prática em suporte, servidores, redes, consultoria e soluções web para reduzir paradas e organizar a tecnologia da sua empresa.</p>
                            <div class="hero-actions">
                                <a class="btn btn-primary" href="#contato"><i class="fa-solid fa-message"></i> Fale conosco</a>
                                <a class="btn btn-outline-light" href="#servicos"><i class="fa-solid fa-arrow-down"></i> Ver serviços</a>
                            </div>
                            <div class="hero-metrics">
                                <div><strong>20+</strong><span>anos de experiência</span></div>
                                <div><strong>5</strong><span>frentes de serviço</span></div>
                                <div><strong>SLA</strong><span>suporte e chamados</span></div>
                            </div>
                        </div>
                        <div class="col-lg-5">
                            <div class="hero-console">
                                <div class="console-top">
                                    <span></span><span></span><span></span>
                                </div>
                                <div class="console-grid">
                                    <div><i class="fa-solid fa-server"></i><span>Servidores</span></div>
                                    <div><i class="fa-solid fa-shield-halved"></i><span>Segurança</span></div>
                                    <div><i class="fa-solid fa-network-wired"></i><span>Redes</span></div>
                                    <div><i class="fa-solid fa-code"></i><span>Web</span></div>
                                </div>
                                <div class="console-status">
                                    <span>Monitoramento ativo</span>
                                    <strong>Operacional</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="servicos" class="section-block services-section">
                <div class="container">
                    <div class="section-heading">
                        <span>Serviços</span>
                        <h2>Serviços</h2>
                        <!-- <p>As áreas abaixo agora vêm do Laravel, então o conteúdo pode ser alterado sem mexer no código do site.</p> -->
                    </div>
                    <div class="service-layout" v-if="activeServiceData">
                        <aside class="service-menu" aria-label="Serviços">
                            <button
                                v-for="service in services"
                                :key="service.id"
                                type="button"
                                :class="{ active: activeService === service.id }"
                                @click="activeService = service.id"
                            >
                                <i class="fa-solid" :class="service.icon"></i>
                                <span>{{ service.label }}</span>
                            </button>
                        </aside>
                        <article class="service-detail">
                            <div class="service-kicker"><i class="fa-solid" :class="activeServiceData.icon"></i> {{ activeServiceData.label }}</div>
                            <h3>{{ activeServiceData.title }}</h3>
                            <p class="service-summary">{{ activeServiceData.summary }}</p>
                            <p v-if="activeServiceData.content" class="service-content">{{ activeServiceData.content }}</p>
                            <div class="feature-grid" v-if="activeServiceData.features?.length">
                                <div v-for="item in activeServiceData.features" :key="item" class="feature-card">
                                    <i class="fa-solid fa-check"></i>
                                    <span>{{ item }}</span>
                                </div>
                            </div>
                        </article>
                    </div>
                </div>
            </section>

            <section id="portfolio" class="section-block portfolio-section">
                <div class="container">
                    <div class="section-heading">
                        <span>Portfólio</span>
                        <h2>Projetos e cases</h2>
                        <!-- <p>Cadastre projetos no painel para publicar cases, imagens e links.</p> -->
                    </div>
                    <div v-if="hasPortfolio" class="portfolio-grid">
                        <article v-for="item in portfolioItems" :key="item.title" class="portfolio-card">
                            <div class="portfolio-image" :style="item.image_url ? { backgroundImage: `url(${item.image_url})` } : {}">
                                <i v-if="!item.image_url" class="fa-solid fa-diagram-project"></i>
                            </div>
                            <div>
                                <span>{{ item.category || 'Projeto' }}</span>
                                <h3>{{ item.title }}</h3>
                                <p>{{ item.description }}</p>
                                <a v-if="item.project_url" :href="item.project_url" target="_blank" rel="noopener">Ver projeto <i class="fa-solid fa-arrow-up-right-from-square"></i></a>
                            </div>
                        </article>
                    </div>
                    <div v-else class="empty-state">
                        <i class="fa-solid fa-briefcase"></i>
                        <strong>Portfólio em preparação</strong>
                        <span>Os próximos projetos publicados no painel aparecerão automaticamente aqui.</span>
                    </div>
                </div>
            </section>

            <section id="artigos" class="section-block articles-section">
                <div class="container">
                    <div class="section-heading">
                        <span>Artigos</span>
                        <h2>Vídeos e conteúdos técnicos</h2>
                        <!-- <p>Publique vídeos do YouTube, dicas e materiais antigos diretamente pela área administrativa.</p> -->
                    </div>
                    <div v-if="hasArticles" class="article-grid">
                        <article v-for="article in articles" :key="article.title" class="article-card">
                            <div v-if="youtubeEmbed(article.youtube_url)" class="video-frame">
                                <iframe :src="youtubeEmbed(article.youtube_url)" :title="article.title" allowfullscreen></iframe>
                            </div>
                            <div v-else class="article-icon"><i class="fa-solid fa-newspaper"></i></div>
                            <h3>{{ article.title }}</h3>
                            <p>{{ article.excerpt || article.body }}</p>
                        </article>
                    </div>
                    <div v-else class="empty-state">
                        <i class="fa-brands fa-youtube"></i>
                        <strong>Artigos em desenvolvimento</strong>
                        <span>Quando você cadastrar vídeos ou textos no painel, eles entram nesta seção.</span>
                    </div>
                </div>
            </section>

            <section id="contato" class="section-block contact-section">
                <div class="container">
                    <div class="contact-shell">
                        <div class="contact-info">
                            <div class="section-heading text-start">
                                <span>Contato</span>
                                <h2>Vamos organizar sua TI?</h2>
                                <p>Envie uma mensagem e conte rapidamente o cenário. O retorno pode ser por e-mail, telefone ou WhatsApp.</p>
                            </div>
                            <div class="contact-list">
                                <a :href="`mailto:${contact.email}`"><i class="fa-solid fa-envelope"></i>{{ contact.email }}</a>
                                <a href="tel:+5585997380835"><i class="fa-solid fa-phone"></i>(85) 99738.0835</a>
                                <a :href="contact.facebook" target="_blank" rel="noopener"><i class="fa-brands fa-facebook"></i>reinformatica.ce</a>
                            </div>
                        </div>
                        <form class="contact-form" method="post" :action="contactAction">
                            <h3>Fale Conosco</h3>
                            <div v-if="flash" class="alert alert-success">{{ flash }}</div>
                            <div v-if="errors.length" class="alert alert-danger">
                                <div v-for="error in errors" :key="error">{{ error }}</div>
                            </div>
                            <input type="hidden" name="_token" :value="csrfToken">
                            <div class="row g-3">
                                <div class="col-12">
                                    <label for="name">Nome *</label>
                                    <PInputText id="name" name="name" class="form-control" required />
                                </div>
                                <div class="col-12">
                                    <label for="email">E-mail *</label>
                                    <PInputText id="email" name="email" type="email" class="form-control" required />
                                </div>
                                <div class="col-md-6">
                                    <label for="phone">Telefone *</label>
                                    <PInputText id="phone" name="phone" class="form-control" required @input="maskPhone" />
                                </div>
                                <div class="col-md-6">
                                    <label for="mobile">Celular *</label>
                                    <PInputText id="mobile" name="mobile" class="form-control" required @input="maskPhone" />
                                </div>
                                <div class="col-12">
                                    <label for="subject">Assunto *</label>
                                    <PInputText id="subject" name="subject" class="form-control" required />
                                </div>
                                <div class="col-12">
                                    <label for="message">Mensagem</label>
                                    <PTextarea id="message" name="message" class="form-control" rows="5" />
                                </div>
                                <div class="col-12">
                                    <PButton class="btn btn-primary w-100" type="submit" label="Enviar mensagem" />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </section>
        </main>

        <footer class="site-footer">
            <div class="container d-flex flex-column flex-md-row justify-content-between gap-3">
                <span>© {{ currentYear }} R. E. Informática. Todos os direitos reservados.</span>
                <!-- <a href="/admin">Área administrativa</a> -->
            </div>
        </footer>

        <a class="whatsapp-float" :href="`https://wa.me/${contact.whatsapp}`" target="_blank" rel="noopener" aria-label="WhatsApp">
            <i class="fa-brands fa-whatsapp"></i>
        </a>
    </div>
</template>
